/**
 * Servicio centralizado para gestionar tareas cron
 * Maneja caché, llamadas API y errores
 */
import axiosInstance from '../../api/axiosInstance'
import type {
  CronTaskStatusResponse,
  CronTaskCountResponse,
  CronTaskExecuteResponse,
  CronTaskExecuteParams,
  HomeCenterScheduledTaskResponse,
  WebhookRetryStatusResponse,
  CronGlobalStatusResponse,
  CronGlobalTaskEntry,
  BoldVerifyLinksExecuteResponse,
  BoldVoucherVerifyExecuteResponse,
  EnviaHubSyncGuiasExecuteResponse,
  CronTaskTimeUntil,
} from '../../types/cron/cronTasks'

interface CacheEntry<T> {
  data: T
  timestamp: number
}

export class CronTasksService {
  private static cache = new Map<string, CacheEntry<any>>()
  private static readonly CACHE_TTL = 5000 // 5 segundos de caché
  private static readonly CRON_GLOBAL_RAW_CACHE_KEY = 'cron-global-status-raw'

  /**
   * Mapa de tareas en GET /api/cron/status (scheduledTasks es el campo documentado; tasks por compatibilidad)
   */
  private static pickGlobalTasksMap(
    response: CronGlobalStatusResponse
  ): Record<string, CronGlobalTaskEntry | undefined> | undefined {
    return response.scheduledTasks ?? response.tasks
  }

  /**
   * Lista de horas (0–23) desde el campo hora de un patrón cron de 5 campos; null si no aplica (ej. paso horario con * o /)
   */
  private static parseHourListFromCron(cronPattern: string): number[] | null {
    const parts = cronPattern.trim().split(/\s+/)
    if (parts.length < 2) return null
    const hourField = parts[1]
    if (hourField.includes('*') || hourField.includes('/')) return null
    const list = hourField
      .split(',')
      .map((x) => parseInt(x.trim(), 10))
      .filter((n) => !Number.isNaN(n) && n >= 0 && n <= 23)
    return list.length ? list : null
  }

  /**
   * Próxima ejecución en :00 (America/Bogota) según lista de horas del día
   */
  private static getNextBogotaSlotFromHourList(hours: number[]): Date {
    const sorted = Array.from(new Set(hours)).sort((a, b) => a - b)
    const bogotaDateStr = new Date().toLocaleString('en-CA', {
      timeZone: 'America/Bogota',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    const [y, m, d] = bogotaDateStr.split('-').map((n) => parseInt(n, 10))
    const nowMs = Date.now()
    for (const h of sorted) {
      const iso = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}T${String(h).padStart(2, '0')}:00:00-05:00`
      const candidate = new Date(iso)
      if (candidate.getTime() > nowMs) return candidate
    }
    const first = sorted[0] ?? 8
    const nextDay = new Date(y, m - 1, d + 1)
    const yy = nextDay.getFullYear()
    const mm = nextDay.getMonth() + 1
    const dd = nextDay.getDate()
    const iso2 = `${yy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}T${String(first).padStart(2, '0')}:00:00-05:00`
    return new Date(iso2)
  }

  private static async fetchCronGlobalStatus(
    endpoint: string
  ): Promise<CronGlobalStatusResponse> {
    const cacheKey = this.CRON_GLOBAL_RAW_CACHE_KEY
    const cached = this.cache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.data as CronGlobalStatusResponse
    }
    const response = await axiosInstance.get(endpoint)
    const data = response.data as CronGlobalStatusResponse
    this.cache.set(cacheKey, {
      data,
      timestamp: Date.now(),
    })
    return data
  }

  /**
   * Convertir formato de fecha "25-12-05 07:00" a ISO
   * Formato: YY-MM-DD HH:mm (año-mes-día hora:minuto en timezone de Bogotá)
   */
  private static parseHomeCenterDate(dateStr: string): Date {
    try {
      // Formato: "25-12-05 07:00" -> "2025-12-05T07:00:00-05:00"
      const parts = dateStr.trim().split(' ')
      if (parts.length !== 2) {
        console.warn(`Formato de fecha inválido: ${dateStr}, usando fecha actual`)
        return new Date()
      }

      const [datePart, timePart] = parts
      const dateComponents = datePart.split('-')
      const timeComponents = timePart.split(':')

      if (dateComponents.length !== 3 || timeComponents.length !== 2) {
        console.warn(`Formato de fecha inválido: ${dateStr}, usando fecha actual`)
        return new Date()
      }

      const [year, month, day] = dateComponents
      const [hour, minute] = timeComponents

      // Convertir año de 2 dígitos a 4 dígitos (asumiendo 20XX)
      const fullYear = parseInt(`20${year}`, 10)
      const monthNum = parseInt(month, 10)
      const dayNum = parseInt(day, 10)
      const hourNum = parseInt(hour, 10)
      const minuteNum = parseInt(minute, 10)

      // Validar que los números sean válidos
      if (
        isNaN(fullYear) ||
        isNaN(monthNum) ||
        isNaN(dayNum) ||
        isNaN(hourNum) ||
        isNaN(minuteNum)
      ) {
        console.warn(`Valores de fecha inválidos: ${dateStr}, usando fecha actual`)
        return new Date()
      }

      // Crear string ISO con timezone de Bogotá (-05:00)
      // Formato: YYYY-MM-DDTHH:mm:ss-05:00
      const isoString = `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:00-05:00`
      
      const date = new Date(isoString)
      
      // Validar que la fecha sea válida
      if (isNaN(date.getTime())) {
        console.warn(`Fecha inválida generada: ${isoString}, usando fecha actual`)
        return new Date()
      }
      
      return date
    } catch (error) {
      console.error(`Error parseando fecha ${dateStr}:`, error)
      return new Date()
    }
  }

  /**
   * Convertir timeUntilNext de número (milliseconds) a objeto
   */
  private static convertTimeUntilNext(
    timeUntilNext: number | {
      hours: number
      minutes: number
      seconds: number
      totalMilliseconds: number
    }
  ): CronTaskTimeUntil {
    // Si ya es un objeto, retornarlo directamente
    if (typeof timeUntilNext === 'object' && timeUntilNext !== null) {
      return {
        hours: timeUntilNext.hours,
        minutes: timeUntilNext.minutes,
        seconds: timeUntilNext.seconds,
        totalMilliseconds: timeUntilNext.totalMilliseconds,
      }
    }

    // Si es un número (milliseconds), convertirlo a objeto
    const totalMs = typeof timeUntilNext === 'number' ? timeUntilNext : 0
    const hours = Math.floor(totalMs / (1000 * 60 * 60))
    const minutes = Math.floor((totalMs % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((totalMs % (1000 * 60)) / 1000)

    return {
      hours,
      minutes,
      seconds,
      totalMilliseconds: totalMs,
    }
  }

  /**
   * Parsear nextExecution: puede venir en formato ISO o formato "25-12-05 07:00"
   */
  private static parseNextExecution(nextExecution: string): Date {
    // Si ya es formato ISO (contiene 'T' o termina en 'Z'), usarlo directamente
    if (nextExecution.includes('T') || nextExecution.endsWith('Z')) {
      const date = new Date(nextExecution)
      if (!isNaN(date.getTime())) {
        return date
      }
    }

    // Si no es ISO, intentar parsear como formato "25-12-05 07:00"
    return this.parseHomeCenterDate(nextExecution)
  }

  /**
   * Adaptador para transformar respuesta de HomeCenter al formato estándar
   */
  private static adaptHomeCenterResponse(
    response: HomeCenterScheduledTaskResponse
  ): CronTaskStatusResponse {
    const task = response.scheduledTasks.homeCenterPendingSync
    
    // Parsear nextExecution (puede venir en ISO o formato "25-12-05 07:00")
    const nextExecutionDate = this.parseNextExecution(task.nextExecution)

    // Convertir timeUntilNext (puede venir como número o objeto)
    const timeUntilNext = this.convertTimeUntilNext(task.timeUntilNext)

    // Extraer horas de ejecución del cronPattern (ej: "0 7 * * *" -> [7])
    const cronParts = task.cronPattern.split(' ')
    const executionHours =
      cronParts.length >= 2
        ? [parseInt(cronParts[1], 10)].filter((h) => !isNaN(h))
        : []

    return {
      success: response.success,
      message: response.message,
      task: {
        enabled: task.enabled,
        frequency: task.frequency,
        cronPattern: task.cronPattern,
        timezone: task.timezone,
        hasTask: task.hasTask,
        nextExecution: task.nextExecution,
        nextExecutionISO: nextExecutionDate.toISOString(),
        timeUntilNext,
        humanReadable: task.humanReadable,
        executionSchedule: {
          hours: executionHours,
          description: task.frequency,
        },
        currentTime: task.currentTime || new Date().toISOString(),
        currentTimeBogota: task.currentTimeBogota || new Date().toLocaleString('es-CO', {
          timeZone: 'America/Bogota',
        }),
      },
      timestamp: new Date().toISOString(),
    }
  }

  /**
   * Adaptador para respuesta de ejecución de HomeCenter
   * Maneja la nueva estructura con status y note
   */
  private static adaptHomeCenterExecuteResponse(
    response: {
      success: boolean
      message: string
      status?: string
      note?: string
    }
  ): CronTaskExecuteResponse {
    // Construir mensaje completo incluyendo note si existe
    let fullMessage = response.message
    if (response.note) {
      fullMessage = `${response.message}. ${response.note}`
    }

    return {
      success: response.success,
      message: fullMessage,
      data: {
        total: 0,
        processed: 0,
        updated: 0,
        errors: 0,
        details: [],
      },
      summary: {
        pedidos_encontrados: 0,
        pedidos_procesados: 0,
        pedidos_actualizados: 0,
        pedidos_con_errores: 0,
        pedidos_sin_cambios: 0,
      },
      timestamp: new Date().toISOString(),
    }
  }

  /**
   * Adaptador para transformar respuesta de Webhook Retry al formato estándar
   * La respuesta no incluye información de la tarea programada, así que generamos valores por defecto
   * También extrae el count de webhooks pendientes de stats.total_pendientes
   */
  private static adaptWebhookRetryResponse(
    response: WebhookRetryStatusResponse
  ): CronTaskStatusResponse {
    // La tarea se ejecuta cada 6 horas (según documentación: 12:00 AM, 6:00 AM, 12:00 PM, 6:00 PM)
    // Calcular la próxima ejecución basada en la hora actual
    const now = new Date()
    const currentHour = now.getHours()
    
    // Determinar la próxima hora de ejecución (0, 6, 12, 18)
    let nextHour = 0
    if (currentHour < 6) {
      nextHour = 6
    } else if (currentHour < 12) {
      nextHour = 12
    } else if (currentHour < 18) {
      nextHour = 18
    } else {
      // Si es después de las 18:00, la próxima es a las 00:00 del día siguiente
      nextHour = 0
      now.setDate(now.getDate() + 1)
    }
    
    // Crear fecha de próxima ejecución
    const nextExecution = new Date(now)
    nextExecution.setHours(nextHour, 0, 0, 0)
    
    // Calcular timeUntilNext
    const timeUntilNextMs = nextExecution.getTime() - Date.now()
    const hours = Math.floor(timeUntilNextMs / (1000 * 60 * 60))
    const minutes = Math.floor((timeUntilNextMs % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((timeUntilNextMs % (1000 * 60)) / 1000)
    
    // Formatear humanReadable
    const parts: string[] = []
    if (hours > 0) parts.push(`${hours}h`)
    if (minutes > 0) parts.push(`${minutes}m`)
    if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`)
    const humanReadable = parts.join(' ')

    return {
      success: response.success,
      message: response.message,
      task: {
        enabled: true, // Asumimos que está habilitada si hay endpoint
        frequency: 'Cada 6 horas (12:00 AM, 6:00 AM, 12:00 PM, 6:00 PM)',
        cronPattern: '0 */6 * * *',
        timezone: 'America/Bogota',
        hasTask: true,
        nextExecution: nextExecution.toISOString(),
        nextExecutionISO: nextExecution.toISOString(),
        timeUntilNext: {
          hours,
          minutes,
          seconds,
          totalMilliseconds: timeUntilNextMs,
        },
        humanReadable,
        executionSchedule: {
          hours: [0, 6, 12, 18],
          description: 'Cada 6 horas',
        },
        currentTime: new Date().toISOString(),
        currentTimeBogota: new Date().toLocaleString('es-CO', {
          timeZone: 'America/Bogota',
        }),
      },
      timestamp: response.timestamp || new Date().toISOString(),
    }
  }

  /**
   * Adaptador para respuesta de ejecución de Webhook Retry
   * Maneja la estructura con pendientes y detalle
   */
  private static adaptWebhookRetryExecuteResponse(
    response: {
      success: boolean
      message: string
      pendientes?: number
      detalle?: string
    }
  ): CronTaskExecuteResponse {
    // Construir mensaje completo incluyendo detalle si existe
    let fullMessage = response.message
    if (response.detalle) {
      fullMessage = `${response.message}. ${response.detalle}`
    }
    if (response.pendientes !== undefined) {
      fullMessage = `${fullMessage} (${response.pendientes} webhooks pendientes)`
    }

    return {
      success: response.success,
      message: fullMessage,
      data: {
        total: response.pendientes || 0,
        processed: 0,
        updated: 0,
        errors: 0,
        details: [],
      },
      summary: {
        pedidos_encontrados: response.pendientes || 0,
        pedidos_procesados: 0,
        pedidos_actualizados: 0,
        pedidos_con_errores: 0,
        pedidos_sin_cambios: 0,
      },
      timestamp: new Date().toISOString(),
    }
  }

  /**
   * Calcular próxima ejecución para cron cada 3 horas (0, 3, 6, 9, 12, 15, 18, 21) en America/Bogota
   */
  private static getNextExecutionEvery3Hours(): Date {
    const now = new Date()
    const bogotaHour = parseInt(
      now.toLocaleString('en-US', { timeZone: 'America/Bogota', hour: 'numeric', hour12: false }),
      10
    )
    const bogotaDateStr = now.toLocaleString('en-CA', {
      timeZone: 'America/Bogota',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    const [y, m, d] = bogotaDateStr.split('-').map((n) => parseInt(n, 10))
    const nextHour = (Math.floor(bogotaHour / 3) + 1) * 3
    const addDay = nextHour >= 24 ? 1 : 0
    const hourVal = nextHour >= 24 ? 0 : nextHour
    const nextDate = new Date(y, m - 1, d + addDay)
    const yy = nextDate.getFullYear()
    const mm = nextDate.getMonth() + 1
    const dd = nextDate.getDate()
    const iso = `${yy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}T${String(hourVal).padStart(2, '0')}:00:00-05:00`
    return new Date(iso)
  }

  /**
   * Entrada en scheduledTasks para cron Envía Hub (Colvanes)
   */
  private static pickEnviaHubSyncEntry(
    response: CronGlobalStatusResponse
  ): CronGlobalTaskEntry | undefined {
    const map = this.pickGlobalTasksMap(response)
    return map?.enviaHubSyncGuias ?? map?.enviaHubGuiaSync
  }

  /**
   * Adaptador GET /cron/status → enviaHubSyncGuias (cada 6 h, America/Bogota)
   */
  private static adaptEnviaHubSyncGuiasStatusResponse(
    response: CronGlobalStatusResponse
  ): CronTaskStatusResponse {
    const entry = this.pickEnviaHubSyncEntry(response)
    const enabled = entry?.enabled ?? true
    const frequency =
      entry?.frequency ?? 'Cada 6 horas (00:00, 06:00, 12:00, 18:00)'
    const hasTask = entry?.hasTask ?? true
    const cronPattern = entry?.cronPattern ?? '0 */6 * * *'
    const timezone = entry?.timezone ?? 'America/Bogota'
    const hourList = this.parseHourListFromCron(cronPattern) ?? [0, 6, 12, 18]

    let nextExecution: Date
    if (entry?.nextExecution != null && entry.nextExecution !== '') {
      nextExecution = this.parseNextExecution(entry.nextExecution)
    } else {
      nextExecution = this.getNextBogotaSlotFromHourList(hourList)
    }

    const nextExecutionISO = nextExecution.toISOString()
    const timeUntilNextMs = Math.max(0, nextExecution.getTime() - Date.now())
    const timeUntilNext: CronTaskTimeUntil = {
      hours: Math.floor(timeUntilNextMs / (1000 * 60 * 60)),
      minutes: Math.floor((timeUntilNextMs % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((timeUntilNextMs % (1000 * 60)) / 1000),
      totalMilliseconds: timeUntilNextMs,
    }
    const parts: string[] = []
    if (timeUntilNext.hours > 0) parts.push(`${timeUntilNext.hours}h`)
    if (timeUntilNext.minutes > 0) parts.push(`${timeUntilNext.minutes}m`)
    if (timeUntilNext.seconds > 0 || parts.length === 0) parts.push(`${timeUntilNext.seconds}s`)
    const humanReadable = parts.join(' ')

    return {
      success: response.success ?? true,
      message: response.message ?? 'OK',
      task: {
        enabled,
        frequency,
        cronPattern,
        timezone,
        hasTask,
        nextExecution: nextExecutionISO,
        nextExecutionISO,
        timeUntilNext,
        humanReadable,
        executionSchedule: {
          hours: hourList,
          description: frequency,
        },
        currentTime: new Date().toISOString(),
        currentTimeBogota: new Date().toLocaleString('es-CO', {
          timeZone: 'America/Bogota',
        }),
      },
      timestamp: response.timestamp ?? new Date().toISOString(),
    }
  }

  /**
   * Adaptador GET /cron/envia-hub-sync-guias
   */
  private static adaptEnviaHubSyncGuiasExecuteResponse(
    response: EnviaHubSyncGuiasExecuteResponse
  ): CronTaskExecuteResponse {
    const d = response.data
    const hubOk = d.hub_ok ?? 0
    const updated = d.updated ?? 0
    const errors = d.errors ?? 0
    const processed = d.processed ?? 0
    const total = d.total ?? 0
    const sinCambio = Math.max(0, hubOk - updated)

    return {
      success: response.success,
      message: response.message,
      data: {
        total,
        processed,
        updated,
        errors,
        details: [],
      },
      summary: {
        pedidos_encontrados: total,
        pedidos_procesados: processed,
        pedidos_actualizados: updated,
        pedidos_con_errores: errors,
        pedidos_sin_cambios: sinCambio,
      },
      timestamp: response.timestamp ?? new Date().toISOString(),
    }
  }

  /**
   * Adaptador para respuesta global GET /cron/status → extrae boldVerifyLinksSync
   */
  private static adaptBoldVerifyLinksStatusResponse(
    response: CronGlobalStatusResponse
  ): CronTaskStatusResponse {
    const entry = this.pickGlobalTasksMap(response)?.boldVerifyLinksSync
    const enabled = entry?.enabled ?? false
    const frequency = entry?.frequency ?? 'Cada 3 horas'
    const hasTask = entry?.hasTask ?? false
    const cronPattern = entry?.cronPattern ?? '0 */3 * * *'
    const timezone = entry?.timezone ?? 'America/Bogota'

    let nextExecution: Date
    if (entry?.nextExecution != null && entry.nextExecution !== '') {
      nextExecution = this.parseNextExecution(entry.nextExecution)
    } else {
      const hourList = this.parseHourListFromCron(cronPattern)
      nextExecution = hourList
        ? this.getNextBogotaSlotFromHourList(hourList)
        : this.getNextExecutionEvery3Hours()
    }
    const nextExecutionISO = nextExecution.toISOString()
    const timeUntilNextMs = Math.max(0, nextExecution.getTime() - Date.now())
    const timeUntilNext: CronTaskTimeUntil = {
      hours: Math.floor(timeUntilNextMs / (1000 * 60 * 60)),
      minutes: Math.floor((timeUntilNextMs % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((timeUntilNextMs % (1000 * 60)) / 1000),
      totalMilliseconds: timeUntilNextMs,
    }
    const parts: string[] = []
    if (timeUntilNext.hours > 0) parts.push(`${timeUntilNext.hours}h`)
    if (timeUntilNext.minutes > 0) parts.push(`${timeUntilNext.minutes}m`)
    if (timeUntilNext.seconds > 0 || parts.length === 0) parts.push(`${timeUntilNext.seconds}s`)
    const humanReadable = parts.join(' ')

    return {
      success: response.success ?? true,
      message: response.message ?? 'OK',
      task: {
        enabled,
        frequency,
        cronPattern,
        timezone,
        hasTask,
        nextExecution: nextExecutionISO,
        nextExecutionISO,
        timeUntilNext,
        humanReadable,
        executionSchedule: {
          hours: this.parseHourListFromCron(cronPattern) ?? [0, 3, 6, 9, 12, 15, 18, 21],
          description: frequency,
        },
        currentTime: new Date().toISOString(),
        currentTimeBogota: new Date().toLocaleString('es-CO', {
          timeZone: 'America/Bogota',
        }),
      },
      timestamp: response.timestamp ?? new Date().toISOString(),
    }
  }

  /**
   * Adaptador para respuesta global GET /cron/status → boldVoucherVerifySync
   */
  private static adaptBoldVoucherVerifyStatusResponse(
    response: CronGlobalStatusResponse
  ): CronTaskStatusResponse {
    const entry = this.pickGlobalTasksMap(response)?.boldVoucherVerifySync
    const enabled = entry?.enabled ?? false
    const frequency =
      entry?.frequency ??
      'Diario 8:00–12:00 y 23:00 (America/Bogota)'
    const hasTask = entry?.hasTask ?? false
    const cronPattern = entry?.cronPattern ?? '0 8,9,10,11,12,23 * * *'
    const timezone = entry?.timezone ?? 'America/Bogota'
    const hourList =
      this.parseHourListFromCron(cronPattern) ?? [8, 9, 10, 11, 12, 23]

    let nextExecution: Date
    if (entry?.nextExecution != null && entry.nextExecution !== '') {
      nextExecution = this.parseNextExecution(entry.nextExecution)
    } else {
      nextExecution = this.getNextBogotaSlotFromHourList(hourList)
    }

    const nextExecutionISO = nextExecution.toISOString()
    const timeUntilNextMs = Math.max(0, nextExecution.getTime() - Date.now())
    const timeUntilNext: CronTaskTimeUntil = {
      hours: Math.floor(timeUntilNextMs / (1000 * 60 * 60)),
      minutes: Math.floor((timeUntilNextMs % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((timeUntilNextMs % (1000 * 60)) / 1000),
      totalMilliseconds: timeUntilNextMs,
    }
    const parts: string[] = []
    if (timeUntilNext.hours > 0) parts.push(`${timeUntilNext.hours}h`)
    if (timeUntilNext.minutes > 0) parts.push(`${timeUntilNext.minutes}m`)
    if (timeUntilNext.seconds > 0 || parts.length === 0) parts.push(`${timeUntilNext.seconds}s`)
    const humanReadable = parts.join(' ')

    return {
      success: response.success ?? true,
      message: response.message ?? 'OK',
      task: {
        enabled,
        frequency,
        cronPattern,
        timezone,
        hasTask,
        nextExecution: nextExecutionISO,
        nextExecutionISO,
        timeUntilNext,
        humanReadable,
        executionSchedule: {
          hours: hourList,
          description: frequency,
        },
        currentTime: new Date().toISOString(),
        currentTimeBogota: new Date().toLocaleString('es-CO', {
          timeZone: 'America/Bogota',
        }),
      },
      timestamp: response.timestamp ?? new Date().toISOString(),
    }
  }

  /**
   * Adaptador para respuesta GET /cron/bold-verify-links
   */
  private static adaptBoldVerifyLinksExecuteResponse(
    response: BoldVerifyLinksExecuteResponse
  ): CronTaskExecuteResponse {
    const d = response.data
    return {
      success: response.success,
      message: response.message,
      data: {
        total: d.total,
        processed: d.updatedIdTransaccion + d.toProcessing + d.toCancelled + d.cancelledByTTL,
        updated: d.updatedIdTransaccion,
        errors: d.errors,
        details: [],
      },
      summary: {
        pedidos_encontrados: d.total,
        pedidos_procesados: d.total - d.errors,
        pedidos_actualizados: d.updatedIdTransaccion + d.toProcessing,
        pedidos_con_errores: d.errors,
        pedidos_sin_cambios: Math.max(0, d.total - d.updatedIdTransaccion - d.toProcessing - d.toCancelled - d.cancelledByTTL - d.errors),
      },
      timestamp: response.timestamp,
    }
  }

  /**
   * Adaptador para respuesta GET /cron/bold-voucher-verify
   */
  private static adaptBoldVoucherVerifyExecuteResponse(
    response: BoldVoucherVerifyExecuteResponse
  ): CronTaskExecuteResponse {
    const raw = response.data
    const checked = raw?.checked ?? 0
    const approved = raw?.approved ?? 0
    const toIncidencia = raw?.toIncidencia ?? 0
    const skippedTransient = raw?.skippedTransient ?? 0
    const skippedNoKey = raw?.skippedNoKey ?? 0
    const skippedNoUser = raw?.skippedNoUser ?? 0
    const updateErrors = raw?.updateErrors ?? 0
    const sinCambio =
      approved + skippedTransient + skippedNoKey + skippedNoUser
    return {
      success: response.success,
      message: response.message,
      data: {
        total: checked,
        processed: checked,
        updated: toIncidencia,
        errors: updateErrors,
        details: [],
      },
      summary: {
        pedidos_encontrados: checked,
        pedidos_procesados: checked,
        pedidos_actualizados: toIncidencia,
        pedidos_con_errores: updateErrors,
        pedidos_sin_cambios: sinCambio,
      },
      timestamp: response.timestamp,
    }
  }

  /**
   * Limpiar caché de una tarea específica o de todas
   */
  static clearCache(taskId?: string): void {
    if (taskId) {
      this.cache.delete(`${taskId}-status`)
      this.cache.delete(`${taskId}-count`)
      if (
        taskId === 'bold-verify-links' ||
        taskId === 'bold-voucher-verify' ||
        taskId === 'envia-hub-sync-guias'
      ) {
        this.cache.delete(this.CRON_GLOBAL_RAW_CACHE_KEY)
      }
    } else {
      this.cache.clear()
    }
  }

  /**
   * Obtener estado de una tarea cron
   * Usa caché para evitar requests duplicados
   */
  static async getTaskStatus(
    taskId: string,
    endpoint: string
  ): Promise<CronTaskStatusResponse> {
    const cacheKey = `${taskId}-status`
    const cached = this.cache.get(cacheKey)

    // Verificar si hay caché válido
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.data
    }

    try {
      // GET /cron/status compartido (Bold, Envía Hub)
      if (
        taskId === 'bold-verify-links' ||
        taskId === 'bold-voucher-verify' ||
        taskId === 'envia-hub-sync-guias'
      ) {
        try {
          const globalResponse = await this.fetchCronGlobalStatus(endpoint)
          const adapted =
            taskId === 'bold-verify-links'
              ? CronTasksService.adaptBoldVerifyLinksStatusResponse(globalResponse)
              : taskId === 'bold-voucher-verify'
                ? CronTasksService.adaptBoldVoucherVerifyStatusResponse(globalResponse)
                : CronTasksService.adaptEnviaHubSyncGuiasStatusResponse(globalResponse)
          CronTasksService.cache.set(cacheKey, {
            data: adapted,
            timestamp: Date.now(),
          })
          return adapted
        } catch (adaptError: any) {
          console.error(`Error adaptando respuesta cron global (${taskId}):`, adaptError)
          console.error('Respuesta del backend:', adaptError?.response?.data ?? adaptError)
          throw new Error(
            `Error adaptando respuesta cron global: ${(adaptError as Error).message}`
          )
        }
      }

      const response = await axiosInstance.get(endpoint)

      // Si es HomeCenter, adaptar la respuesta
      if (taskId === 'homecenter-sync-pending') {
        try {
          const adapted = this.adaptHomeCenterResponse(
            response.data as HomeCenterScheduledTaskResponse
          )
          // Actualizar caché con respuesta adaptada
          this.cache.set(cacheKey, {
            data: adapted,
            timestamp: Date.now(),
          })
          return adapted
        } catch (adaptError: any) {
          console.error(`Error adaptando respuesta de HomeCenter:`, adaptError)
          console.error('Respuesta del backend:', response.data)
          throw new Error(
            `Error adaptando respuesta de HomeCenter: ${adaptError.message}`
          )
        }
      }

      // Si es Webhook Retry, adaptar la respuesta
      if (taskId === 'webhook-retry-failed') {
        try {
          const webhookResponse = response.data as WebhookRetryStatusResponse
          const adapted = this.adaptWebhookRetryResponse(webhookResponse)
          // Agregar webhookCount a la respuesta para uso en el hook
          const adaptedWithCount = {
            ...adapted,
            webhookCount: webhookResponse.stats.total_pendientes,
          }
          // Actualizar caché con respuesta adaptada
          this.cache.set(cacheKey, {
            data: adaptedWithCount,
            timestamp: Date.now(),
          })
          return adaptedWithCount as CronTaskStatusResponse
        } catch (adaptError: any) {
          console.error(`Error adaptando respuesta de Webhook Retry:`, adaptError)
          console.error('Respuesta del backend:', response.data)
          throw new Error(
            `Error adaptando respuesta de Webhook Retry: ${adaptError.message}`
          )
        }
      }

      // Para otras tareas, usar formato estándar
      const standardResponse = response.data as CronTaskStatusResponse
      this.cache.set(cacheKey, {
        data: standardResponse,
        timestamp: Date.now(),
      })
      return standardResponse
    } catch (error: any) {
      console.error(`Error obteniendo estado de tarea ${taskId}:`, error)
      throw new Error(
        error.response?.data?.message ||
          `Error al obtener estado de la tarea: ${error.message}`
      )
    }
  }

  /**
   * Contar pedidos pendientes de una tarea (si aplica)
   */
  static async getTaskCount(
    taskId: string,
    endpoint: string
  ): Promise<CronTaskCountResponse> {
    const cacheKey = `${taskId}-count`
    const cached = this.cache.get(cacheKey)

    // Verificar si hay caché válido
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.data
    }

    try {
      const response = await axiosInstance.get<CronTaskCountResponse>(endpoint)
      
      // Actualizar caché
      this.cache.set(cacheKey, {
        data: response.data,
        timestamp: Date.now(),
      })

      return response.data
    } catch (error: any) {
      // Silenciar errores de count (no todas las tareas tienen este endpoint)
      console.warn(`Error obteniendo count de tarea ${taskId}:`, error)
      throw error
    }
  }

  /**
   * Ejecutar una tarea cron manualmente
   * Limpia el caché al ejecutar para forzar actualización
   * executeMethod: 'GET' para tareas Bold (verify links, voucher verify), por defecto 'POST'
   */
  static async executeTask(
    taskId: string,
    endpoint: string,
    params?: CronTaskExecuteParams,
    executeMethod: 'GET' | 'POST' = 'POST'
  ): Promise<CronTaskExecuteResponse> {
    try {
      // Invalidar caché antes de ejecutar
      this.clearCache(taskId)

      const cleanParams: CronTaskExecuteParams = params
        ? {
            limit: params.limit,
            batchSize: params.batchSize,
          }
        : {}

      const response =
        executeMethod === 'GET'
          ? await axiosInstance.get(endpoint)
          : await axiosInstance.post(endpoint, cleanParams)

      // Si es HomeCenter, adaptar la respuesta
      if (taskId === 'homecenter-sync-pending') {
        return this.adaptHomeCenterExecuteResponse(response.data)
      }

      // Si es Webhook Retry, adaptar la respuesta
      if (taskId === 'webhook-retry-failed') {
        return this.adaptWebhookRetryExecuteResponse(response.data)
      }

      // Si es Bold Verify Links, adaptar la respuesta
      if (taskId === 'bold-verify-links') {
        return this.adaptBoldVerifyLinksExecuteResponse(
          response.data as BoldVerifyLinksExecuteResponse
        )
      }

      if (taskId === 'bold-voucher-verify') {
        return this.adaptBoldVoucherVerifyExecuteResponse(
          response.data as BoldVoucherVerifyExecuteResponse
        )
      }

      if (taskId === 'envia-hub-sync-guias') {
        return this.adaptEnviaHubSyncGuiasExecuteResponse(
          response.data as EnviaHubSyncGuiasExecuteResponse
        )
      }

      // Para otras tareas, usar formato estándar
      return response.data as CronTaskExecuteResponse
    } catch (error: any) {
      console.error(`Error ejecutando tarea ${taskId}:`, error)
      throw new Error(
        error.response?.data?.message ||
          `Error al ejecutar la tarea: ${error.message}`
      )
    }
  }
}

